import { readItemField, readInventoryTemplateId } from './item-field-reader';
import type { ItemFieldRow, ItemFieldDependencies } from './item-field-reader';
import { createStatusLayerReadContext } from './status-row-construction';
import type { StatusOriginLayerDependencies } from './status-row-construction';
import { createVitalityReader, hydrateVitality } from './vitality-reader';
import type { FormulaVariableReaders } from './formula-variable-resolution';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity';
import type { InventoryTemplateQuery } from './inventory-template-identity';

export interface FormulaReadDependencies extends StatusOriginLayerDependencies, Omit<ItemFieldDependencies, 'resolveCarrierRef'> {
    /** Optional actual definition query result for the pure template-identity memo.
     * Explicit pre-resolved identity inputs take precedence when supplied.
     */
    queryTemplateDefinition?: InventoryTemplateQuery;
}
const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (row: unknown, key: string): unknown => arrayLike(row) && Object.prototype.propertyIsEnumerable.call(row, key)
    ? (row as Readonly<Record<string, unknown>>)[key] : undefined;

/** Share the scalar/list/provider/inventory guard with vitality and item reads
 * for one formula/score request. No runtime query or mutable result memo is added.
 */
export function createFormulaReadContext(deps: FormulaReadDependencies): FormulaVariableReaders {
    const context = createStatusLayerReadContext(deps);
    const identity = deps.resolveInventoryIdentity ?? (deps.queryTemplateDefinition ? createInventoryTemplateIdentityResolver(deps.queryTemplateDefinition) : undefined);
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const vitality = createVitalityReader({ inventoryContext: context.inventoryContext,
        resolveInventoryInstance: id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); } });
    return { layerValue: context.layerValue, layerEntries: (row,name) => row === null ? [] : context.layerEntries(row,name),
        itemField: (row,key) => readItemField(row,key,{
            resolveCarrierRef: id => { emit(['reference', id]); return deps.resolveCarrierRef(id); },
            ...(deps.resolveItemName ? { resolveItemName: (id:string) => { emit(['name',id]); return deps.resolveItemName!(id); } } : {}),
            ...(identity ? { resolveInventoryIdentity: (id:string) => { emit(['inventory-identity',id]); return identity(id); } } : {}),
        }),
        computedValue: (row,bucketName,key) => {
            if (row === null) return null;
            if (!arrayLike(row)) throw new TypeError('Computed reader requires a PHP array or null');
            if (bucketName !== 'vitality') throw new TypeError('Formula computed reader supports its actual vitality call only');
            let value: unknown;
            if (readInventoryTemplateId(row) !== null) {
                const resolved = vitality(row);
                if (arrayLike(resolved)) { value = own(resolved,key) ?? null; return arrayLike(value) && own(value,'0') !== undefined ? own(value,'0') : value; }
            }
            const computed = own(row,'computed');
            const bucket = [own(computed,'vitality'),own(computed,'v_arr'),own(row,'vitality'),own(row,'v_arr')].find(arrayLike);
            value = own(bucket,key) ?? null;
            if (value === null && arrayLike(bucket) && Object.keys(bucket).length) value = own(hydrateVitality(bucket),key) ?? null;
            return arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value,'0') ? own(value,'0') : value;
        },
    };
}
